/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLanguage } from "../context/LanguageContext";
import { getOptimizedImageUrl } from "../utils/image";
import { BRAND_IMAGES } from "../data";
import { Sparkles, Youtube, BookOpen, Volume2, VolumeX, Shield, Sun, Eye, ChevronLeft, Compass, Menu, X, Flame, Home } from "lucide-react";

interface HeaderProps {
  meditationMode: boolean;
  isAudioPlaying: boolean;
  isHafezOpen: boolean;
  activeView: "home" | "videos" | "articles" | "quiz" | "haftkhan";
  onNavigate: (view: string) => void;
  onToggleLang: () => void;
  onToggleMeditationSound: () => void;
  onToggleMeditationMode: () => void;
  onOpenHafez: () => void;
  setIsMobileMenuOpen: (open: boolean) => void;
  isMobileMenuOpen: boolean;
}

export default function Header({
  meditationMode,
  isAudioPlaying,
  isHafezOpen,
  activeView,
  onNavigate,
  onToggleLang,
  onToggleMeditationSound,
  onToggleMeditationMode,
  onOpenHafez,
  setIsMobileMenuOpen,
  isMobileMenuOpen,
}: HeaderProps) {
  const { lang, setLang, isRtl, t } = useLanguage();
  
  // Scroll to top on activeView change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeView]);

  const handleNavClick = useCallback((view: string) => {
    onNavigate(view);
    setIsMobileMenuOpen(false);
  }, [onNavigate, setIsMobileMenuOpen]);

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-[#d4af37]/30 px-4 md:px-8 flex flex-col md:flex-row md:h-24 items-stretch justify-between transition-all bg-[#0d0906] overflow-hidden relative"
    >
      {/* Real Img Background for LCP Discovery & fetchPriority */}
      <img
        src={getOptimizedImageUrl(BRAND_IMAGES.headerBannerBg, 800, 75)}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none opacity-50 z-0"
        loading="eager"
        fetchPriority="high"
      />
      {/* Dark Linear-Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/75 z-0 pointer-events-none select-none" />

      {/* Row 1: Logo & Brand + Mobile Quick Controls */}
      <div className="flex items-center justify-between md:justify-start gap-4 select-none self-center py-2 md:py-0 w-full md:w-auto relative z-10">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 md:w-16 md:h-16 flex items-center justify-center">
            <img
              src={getOptimizedImageUrl(BRAND_IMAGES.brandSeal, 100)}
              alt="RazoRavan Logo"
              className="w-full h-full object-contain hover:scale-110 transition-transform duration-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
              loading="eager"
              fetchPriority="high"
            />
          </div>
          <div className="text-start">
            <h1 className="text-sm md:text-lg font-black text-[#ebd3a8] font-sans leading-none tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
              {t("brandName")}
            </h1>
            <span className="text-[9px] md:text-[10px] text-[#d4af37] font-sans font-bold tracking-widest block mt-1 md:mt-1.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
              {t("brandSubtitle")}
            </span>
          </div>
        </div>

        {/* Controls on Mobile (Next to logo) */}
        <div className="flex md:hidden items-center gap-1.5">
          {/* Language Switcher */}
          <button
            onClick={onToggleLang}
            className="px-2.5 py-1 rounded-full text-[9px] font-bold transition-all border border-[#d4af37]/20 text-[#d4af37] bg-black/60 hover:bg-[#d4af37]/10 cursor-pointer uppercase tracking-wider font-sans"
            title={lang === "fa" ? "Switch to English" : "تغییر به فارسی"}
          >
            {lang === "fa" ? "EN" : "FA"}
          </button>

          {/* Sound Controls (Only visible when meditation mode is active) */}
          {meditationMode && (
            <button
              onClick={onToggleMeditationSound}
              className={`p-1.5 rounded-full border text-xs flex items-center justify-center cursor-pointer transition-all ${
                isAudioPlaying
                  ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]"
                  : "bg-black/60 border-[#d4af37]/10 text-gray-500"
              }`}
              title={isAudioPlaying ? t("muteSound") : t("unmuteSound")}
            >
              {isAudioPlaying ? <Volume2 className="w-3.5 h-3.5 text-[#d4af37]" /> : <VolumeX className="w-3.5 h-3.5 text-[#d4af37]/50" />}
            </button>
          )}

          {/* Hafez Fortune Mobile Button */}
          <button
            onClick={onOpenHafez}
            className="p-1.5 rounded-full text-xs transition-all border border-[#d4af37]/20 text-amber-300 bg-black/60 hover:bg-[#d4af37]/10 cursor-pointer"
            title={isRtl ? "فال حافظ" : "Hafez Fortune"}
          >
            <BookOpen className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
          </button>

          {/* Deep Reflection Mode Button */}
          <button
            onClick={onToggleMeditationMode}
            className={`p-1.5 rounded-full text-xs transition-all border cursor-pointer ${
              meditationMode
                ? "bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37]"
                : "bg-black/60 border-[#d4af37]/10 text-gray-400"
            }`}
            title={meditationMode ? t("deepReflectionActive") : t("deepReflectionInactive")}
          >
            <Sparkles className={`w-3.5 h-3.5 ${meditationMode ? "text-[#d4af37]" : "text-gray-500"}`} />
          </button>
        </div>
      </div>

      {/* Row 2 on Mobile / Center Section on Desktop: Core Navigation Links aligned at the bottom */}
      <nav className="w-full md:w-auto overflow-x-auto overflow-y-hidden md:overflow-visible no-scrollbar flex items-end justify-start md:justify-center text-xs font-semibold text-[#d4af37]/80 font-sans pb-0 relative z-10">
        <div className="flex items-end gap-1.5 md:gap-2 min-w-max pb-0 px-1 md:px-0">
          <button
            onClick={() => handleNavClick("home")}
            className={`relative px-3.5 md:px-4 pb-2 md:pb-2.5 pt-3 md:pt-3.5 text-[11px] md:text-xs font-bold font-sans transition-all duration-300 cursor-pointer flex items-center gap-1.5 md:gap-2 focus:outline-none rounded-t-xl border-t border-x -mb-[1px] select-none ${
              activeView === "home"
                ? "bg-[#4a3a2b]/60 backdrop-blur-md border-amber-400/50 text-amber-100 shadow-[0_-4px_20px_rgba(251,191,36,0.25)]"
                : "bg-white/5 border-transparent text-[#d4af37]/70 hover:text-amber-100 hover:bg-[#3d2e20]/40 hover:border-amber-400/30 hover:-translate-y-[2px]"
            }`}
          >
            {activeView === "home" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 rounded-t-full shadow-[0_-2px_10px_rgba(251,191,36,0.8)]"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            {activeView === "home" && (
              <motion.div
                layoutId="activeHeaderBg"
                className="absolute inset-0 bg-gradient-to-t from-amber-500/15 via-amber-500/5 to-transparent rounded-t-xl -z-10"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            <Home className={`w-3.5 h-3.5 transition-transform duration-300 ${activeView === "home" ? "scale-110 text-amber-400" : "text-amber-500/60"}`} />
            <span className="relative z-10">{t("navHome")}</span>
          </button>

          <button
            onClick={() => handleNavClick("videos")}
            className={`relative px-3.5 md:px-4 pb-2 md:pb-2.5 pt-3 md:pt-3.5 text-[11px] md:text-xs font-bold font-sans transition-all duration-300 cursor-pointer flex items-center gap-1.5 md:gap-2 focus:outline-none rounded-t-xl border-t border-x -mb-[1px] select-none ${
              activeView === "videos"
                ? "bg-[#4a3a2b]/60 backdrop-blur-md border-amber-400/50 text-amber-100 shadow-[0_-4px_20px_rgba(251,191,36,0.25)]"
                : "bg-white/5 border-transparent text-[#d4af37]/70 hover:text-amber-100 hover:bg-[#3d2e20]/40 hover:border-amber-400/30 hover:-translate-y-[2px]"
            }`}
          >
            {activeView === "videos" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 rounded-t-full shadow-[0_-2px_10px_rgba(251,191,36,0.8)]"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            {activeView === "videos" && (
              <motion.div
                layoutId="activeHeaderBg"
                className="absolute inset-0 bg-gradient-to-t from-amber-500/15 via-amber-500/5 to-transparent rounded-t-xl -z-10"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            <Youtube className={`w-3.5 h-3.5 transition-transform duration-300 ${activeView === "videos" ? "scale-110 text-red-400" : "text-red-500/60"}`} />
            <span className="relative z-10">{t("navVideos")}</span>
          </button>

          <button
            onClick={() => handleNavClick("articles")}
            className={`relative px-3.5 md:px-4 pb-2 md:pb-2.5 pt-3 md:pt-3.5 text-[11px] md:text-xs font-bold font-sans transition-all duration-300 cursor-pointer flex items-center gap-1.5 md:gap-2 focus:outline-none rounded-t-xl border-t border-x -mb-[1px] select-none ${
              activeView === "articles"
                ? "bg-[#4a3a2b]/60 backdrop-blur-md border-amber-400/50 text-amber-100 shadow-[0_-4px_20px_rgba(251,191,36,0.25)]"
                : "bg-white/5 border-transparent text-[#d4af37]/70 hover:text-amber-100 hover:bg-[#3d2e20]/40 hover:border-amber-400/30 hover:-translate-y-[2px]"
            }`}
          >
            {activeView === "articles" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 rounded-t-full shadow-[0_-2px_10px_rgba(251,191,36,0.8)]"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            {activeView === "articles" && (
              <motion.div
                layoutId="activeHeaderBg"
                className="absolute inset-0 bg-gradient-to-t from-amber-500/15 via-amber-500/5 to-transparent rounded-t-xl -z-10"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            <BookOpen className={`w-3.5 h-3.5 transition-transform duration-300 ${activeView === "articles" ? "scale-110 text-amber-400" : "text-amber-500/60"}`} />
            <span className="relative z-10">{t("navArticles")}</span>
          </button>

          <button
            onClick={() => handleNavClick("quiz")}
            className={`relative px-3.5 md:px-4 pb-2 md:pb-2.5 pt-3 md:pt-3.5 text-[11px] md:text-xs font-bold font-sans transition-all duration-300 cursor-pointer flex items-center gap-1.5 md:gap-2 focus:outline-none rounded-t-xl border-t border-x -mb-[1px] select-none ${
              activeView === "quiz"
                ? "bg-[#4a3a2b]/60 backdrop-blur-md border-amber-400/50 text-amber-100 shadow-[0_-4px_20px_rgba(251,191,36,0.25)]"
                : "bg-white/5 border-transparent text-[#d4af37]/70 hover:text-amber-100 hover:bg-[#3d2e20]/40 hover:border-amber-400/30 hover:-translate-y-[2px]"
            }`}
          >
            {activeView === "quiz" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 rounded-t-full shadow-[0_-2px_10px_rgba(251,191,36,0.8)]"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            {activeView === "quiz" && (
              <motion.div
                layoutId="activeHeaderBg"
                className="absolute inset-0 bg-gradient-to-t from-amber-500/15 via-amber-500/5 to-transparent rounded-t-xl -z-10"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            <Compass className={`w-3.5 h-3.5 transition-transform duration-300 ${activeView === "quiz" ? "scale-110 text-amber-400 animate-spin-slow" : "text-amber-500/60"}`} />
            <span className="relative z-10">{t("navQuiz")}</span>
          </button>

          <button
            onClick={() => handleNavClick("haftkhan")}
            className={`relative px-3.5 md:px-4 pb-2 md:pb-2.5 pt-3 md:pt-3.5 text-[11px] md:text-xs font-bold font-sans transition-all duration-300 cursor-pointer flex items-center gap-1.5 md:gap-2 focus:outline-none rounded-t-xl border-t border-x -mb-[1px] select-none ${
              activeView === "haftkhan"
                ? "bg-[#4a3a2b]/60 backdrop-blur-md border-amber-400/50 text-amber-100 shadow-[0_-4px_20px_rgba(251,191,36,0.25)]"
                : "bg-white/5 border-transparent text-[#d4af37]/70 hover:text-amber-100 hover:bg-[#3d2e20]/40 hover:border-amber-400/30 hover:-translate-y-[2px]"
            }`}
          >
            {activeView === "haftkhan" && (
              <motion.div
                layoutId="activeHeaderTab"
                className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 rounded-t-full shadow-[0_-2px_10px_rgba(251,191,36,0.8)]"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            {activeView === "haftkhan" && (
              <motion.div
                layoutId="activeHeaderBg"
                className="absolute inset-0 bg-gradient-to-t from-amber-500/15 via-amber-500/5 to-transparent rounded-t-xl -z-10"
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            )}
            <Flame className={`w-3.5 h-3.5 transition-transform duration-300 ${activeView === "haftkhan" ? "scale-110 text-amber-400" : "text-amber-500/60"}`} />
            <span className="relative z-10">{t("navHaftKhan")}</span>
          </button>
        </div>
      </nav>

      {/* Desktop Controls (Hidden on mobile because they are in Row 1) */}
      <div className="hidden md:flex items-center gap-2 self-center relative z-10">
        {/* Language Switcher Button */}
        <button
          onClick={onToggleLang}
          className="px-2.5 py-1 rounded-full text-[10px] font-bold transition-all border border-[#d4af37]/20 text-[#d4af37] bg-[#141414] hover:bg-[#d4af37]/10 cursor-pointer uppercase tracking-wider font-sans"
          title={lang === "fa" ? "Switch to English" : "تغییر به فارسی"}
        >
          {lang === "fa" ? "EN" : "FA"}
        </button>

        {/* Sound Controls (Only visible when meditation mode is active) */}
        <AnimatePresence>
          {meditationMode && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8, x: 10 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 10 }}
              onClick={onToggleMeditationSound}
              className={`p-2 rounded-full border text-xs flex items-center justify-center cursor-pointer transition-all ${
                isAudioPlaying
                  ? "bg-[#d4af37]/10 border-[#d4af37] text-[#d4af37] shadow-[0_0_8px_rgba(212,175,55,0.1)]"
                  : "bg-[#181818] border-[#d4af37]/10 text-gray-500 hover:text-amber-200"
              }`}
              title={isAudioPlaying ? t("muteSound") : t("unmuteSound")}
            >
              {isAudioPlaying ? <Volume2 className="w-4 h-4 animate-bounce text-[#d4af37]" /> : <VolumeX className="w-4 h-4 text-[#d4af37]/50" />}
            </motion.button>
          )}
        </AnimatePresence>

        {/* Hafez Fortune Button */}
        <button
          onClick={onOpenHafez}
          className="flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border border-[#d4af37]/20 text-amber-300 hover:text-amber-100 hover:border-[#d4af37]/40 bg-[#141414] cursor-pointer font-sans"
        >
          <BookOpen className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
          <span>{isRtl ? "فال حافظ" : "Hafez Fortune"}</span>
        </button>

        {/* Deep Reflection Mode Button */}
        <button
          onClick={onToggleMeditationMode}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border cursor-pointer font-sans ${
            meditationMode
              ? "bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37] shadow-[0_0_12px_rgba(212,175,55,0.15)]"
              : "bg-[#141414] border-[#d4af37]/10 text-gray-400 hover:text-amber-200 hover:border-[#d4af37]/30"
          }`}
        >
          <Sparkles className={`w-3.5 h-3.5 ${meditationMode ? "text-[#d4af37]" : "text-gray-500"}`} />
          <span>{meditationMode ? t("deepReflectionActive") : t("deepReflectionInactive")}</span>
        </button>
      </div>
    </header>
  );
}